import { type AssetSheetTabProblem, EventType } from '@atlas/types'
import { ErrorState, ListSkeleton } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { useAssetSheetActivity } from '@/hooks/useAssetSheet'
import { useUserNames } from '@/hooks/useUserNames'
import { apiErrorMessage } from '@/lib/api-error'
import { formatShortDateTime } from '@/lib/format-short-date-time'

/** A run's tab problems, tabs sharing a reason in one clause: `. Tabs ES, DE: Missing mapped column(s): …`. */
function describeTabs(problems: unknown): string {
	if (!Array.isArray(problems)) return ''
	const tabsByReason = new Map<string, string[]>()
	for (const { tab, reason } of problems as AssetSheetTabProblem[]) {
		tabsByReason.set(reason, [...(tabsByReason.get(reason) ?? []), tab])
	}
	return [...tabsByReason]
		.map(([reason, tabs]) => `. ${tabs.length > 1 ? 'Tabs' : 'Tab'} ${tabs.join(', ')}: ${reason}`)
		.join('')
}

/** What a column map change set, in the words the edit dialog uses. */
const FIELD_NAMES: Record<string, string> = {
	columnName: 'header',
	valueCode: 'file type',
	level: 'level',
	cellFormat: 'reading rules',
}

/** `Column map changed by Ana: Packshots on tabs US, ES (header, file type)`. */
function describeColumnChange(data: Record<string, unknown>, by: string | undefined): string {
	const tabs = Array.isArray(data.tabs) ? data.tabs.map(String) : []
	const fields = Object.keys(data.changes ?? {}).map((f) => FIELD_NAMES[f] ?? f)
	return `Column map changed${by ? ` by ${by}` : ''}: ${data.columnName} on ${tabs.length > 1 ? 'tabs' : 'tab'} ${tabs.join(', ')} (${fields.join(', ')})`
}

function describeEvent(type: string, data: Record<string, unknown>, by: string | undefined): string {
	switch (type) {
		case EventType.ASSET_SHEET_SYNC_COMPLETED:
			return `Sync completed: ${data.created} new, ${data.changed} changed, ${data.blocked} blocked, ${data.removed} removed rows${describeTabs(data.tabProblems)}`
		case EventType.ASSET_SHEET_SYNC_FAILED:
			return `Sync failed: ${data.reason}${describeTabs(data.tabProblems)}`
		case EventType.ASSET_SHEET_COLUMN_MAP_UPDATED:
			return describeColumnChange(data, by)
		default:
			return type
	}
}

/** The latest sync outcomes and column map changes, with who changed the map. */
export function ActivityList() {
	const activity = useAssetSheetActivity()
	const names = useUserNames()
	const events = activity.data?.data ?? []
	return (
		<section aria-label="Recent activity" className="flex flex-col gap-ds-2">
			<h2 className="font-ds-display text-ds-lg text-ds-text">Recent activity</h2>
			{activity.isPending ? (
				<ListSkeleton label="Loading recent activity" />
			) : activity.isError ? (
				<ErrorState
					title="Could not load the recent activity"
					description={apiErrorMessage(activity.error, 'Try again.')}
					action={
						<Button variant="secondary" size="sm" onPress={() => void activity.refetch()}>
							Try again
						</Button>
					}
				/>
			) : events.length === 0 ? (
				<p className="text-ds-sm text-ds-text-muted">No sync yet.</p>
			) : (
				// One grid for every line, so each description starts where the widest timestamp ends.
				<ul className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-ds-3 gap-y-ds-1">
					{events.map((e) => (
						<li key={e.eventId} className="col-span-2 grid grid-cols-subgrid text-ds-sm">
							<span className="text-ds-text-muted tabular-nums">{formatShortDateTime(e.timestamp)}</span>
							<span className="min-w-0 wrap-anywhere text-ds-text">
								{describeEvent(e.type, e.data, e.userId && names.get(e.userId))}
							</span>
						</li>
					))}
				</ul>
			)}
		</section>
	)
}
